import { useState, useEffect, useCallback, useMemo } from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import apiClient from '../../services/api/apiClient';
import { Spinner } from 'react-bootstrap';
import ClientSelect from '../../components/select/ClientSelect';
import { IClient } from '../../utils/types/IClient';
import { useAuth } from '../../utils/context/AuthContext';
import { Carte } from '../../utils/types/Carte';
import CarteGrid from '../../components/grid/CarteGrid';
import MultiAllocationModal from '../../components/modals/wizard/carte/MultiAllocationModal';
import { Helmet } from 'react-helmet-async';
import CarteReport from '../../components/modals/reports/CarteReport';
import BatchCartesModal from '../../components/modals/wizard/carte/BatchCartesModal';
import ImportCartesModal from '../../components/modals/wizard/carte/ImportCartesModal';
import { useQuery } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../components/others/QueryErrorAlerts';

const CartePage = () => {
    const { user } = useAuth();

    // ---

    const [selectedCardCode, setSelectedCardCode] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string | null>(null);

    // ---

    const {
        data: clients = [],
        isLoading: isLoadingClients,
        isFetching: isFetchingClients,
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 10, // 10 mins
    });

    const {
        data: cartes = [],
        isLoading: isLoadingCartes,
        refetch: refetchCartes,
        isError: isCartesError,
        error: cartesError,
    } = useQuery<Carte[]>({
        queryKey: [
            'cartes',
            {
                customerCardCode: selectedCardCode,
                searchTerm: searchTerm,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/cartes/getData', {
                params: {
                    customerCardCode: selectedCardCode,
                    searchTerm: searchTerm,
                },
            });
            return res.data ?? [];
        },
        meta: { title: 'Chargement des cartes' },
        enabled: !!selectedCardCode,
        staleTime: 1000 * 60 * 1, // 1 min
    });

    // ---

    const handleLoadCartes = useCallback(() => {
        if (selectedCardCode) {
            refetchCartes();
        }
    }, [selectedCardCode, refetchCartes]);

    // ---

    useEffect(() => {
        if (clients.length === 1) {
            setSelectedCardCode(clients[0].cardCode);
        }
    }, [clients]);

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients },
            { isError: isCartesError, error: cartesError, title: 'Erreur lors du chargement des cartes', refetch: refetchCartes },
        ],
        [isClientsError, clientsError, refetchClients, isCartesError, cartesError, refetchCartes],
    );

    // ---

    return (
        <>
            <Helmet>
                <title>Cartes :: Portail PETROSIL</title>
            </Helmet>

            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col-12 col-md">
                            <div className="page-pretitle">Listing</div>
                            <h2 className="page-title">Cartes</h2>
                        </div>

                        <div className="col-12 col-md-auto">
                            <div className="d-flex flex-wrap justify-content-md-end gap-2">
                                {!user?.roles?.includes('AdminViewer') && <MultiAllocationModal onUpdate={handleLoadCartes} />}

                                <CarteReport />

                                {user?.roles?.includes('Admin') && (
                                    <>
                                        <BatchCartesModal onSuccess={handleLoadCartes} />
                                        <ImportCartesModal onSuccess={handleLoadCartes} />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <QueryErrorAlerts queries={queryErrors} />

                    <div className="card">
                        <div className="accordion accordion-flush" id="accordion-flush">
                            <div className="accordion-item">
                                <button className="accordion-header" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-1-flush" aria-expanded="true">
                                    <div className="accordion-header-text">
                                        <h4>Filtre</h4>
                                    </div>
                                    <div className="accordion-header-toggle">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-1">
                                            <path d="M6 9l6 6l6 -6"></path>
                                        </svg>
                                    </div>
                                </button>
                                <div id="collapse-1-flush" className="accordion-collapse collapse show" data-bs-parent="#accordion-flush">
                                    <div className="accordion-body">
                                        <div>
                                            <div className="row">
                                                <div className="col-12 col-sm-6 col-lg-3">
                                                    <label htmlFor="SelectedCardCode">Client *</label>
                                                    <ClientSelect clients={clients} isClientsLoading={isLoadingClients || isFetchingClients} selectedCardCode={selectedCardCode} setSelectedCardCode={setSelectedCardCode} />
                                                </div>

                                                <div className="col-12 col-sm-6 col-lg-3">
                                                    <label htmlFor="DateFin">Terme de recherche</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={searchTerm ?? ''}
                                                        onChange={(e) => {
                                                            setSearchTerm(e.target.value);
                                                        }}
                                                        placeholder="Numéro, porteur, etc."
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-group mt-2">
                                                <button type="button" className="btn btn-default" disabled={isLoadingCartes} onClick={handleLoadCartes}>
                                                    {isLoadingCartes ? (
                                                        <>
                                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                            &nbsp; Générer
                                                        </>
                                                    ) : (
                                                        'Générer'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-2">
                        <CarteGrid cartes={cartes} codeClient={selectedCardCode ?? ''} handleCarteUpdated={handleLoadCartes} />
                    </div>
                </div>
            </div>
        </>
    );
};

CartePage.Layout = DefaultLayout;

export default CartePage;
